/**
 * Hamburger Menu Component
 * Consolidates all nightclub controls into a sleek slide-out menu
 * Draggable in fullscreen mode
 */
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  Menu, X, Sparkles, Music, Mic, MicOff, Award, Trophy, Camera, Swords, ListMusic,
  DoorOpen, User, Users, Shield, Sun, Wine, Gift, Lock, Unlock, Megaphone, Cloud, Palette, Edit3,
  Volume2, VolumeX, Settings, Play, Pause, SkipForward, SkipBack, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { LIGHTING_PRESETS } from './lightingSystem';

const HamburgerMenu = ({
  setShowEmoteMenu,
  setShowQuestPanel,
  setShowLeaderboard,
  setShowRoomMenu,
  setShowAvatarEditor,
  setShowAdminPanel,
  setShowVirtualBar,
  setShowDailyRewards,
  setShowPrivateRooms,
  setShowAnnouncement,
  voiceChatEnabled,
  isMicMuted,
  toggleMic,
  isAdmin,
  isDJ,
  vibecoins = 0,
  dailyBonusClaimed,
  soundFX,
  currentRoom,
  lightingFx = {},
  onLightingFxChange,
  lightingPreset = 'rave',
  onLightingPresetChange,
  autoCycleLights = false,
  onAutoCycleChange,
  onDropTrigger,
  slowMode = false,
  onSlowModeToggle,
  roomLocked = false,
  onRoomLockToggle,
  onOpenRoomCreator,
  onOpenPhotoMode,
  onOpenDJQueue,
  onOpenDanceBattle,
  onOpenFriendsPanel,
  weatherEffect,
  setWeatherEffect,
  isFullscreen = false,
  visualizerSettings = {},
  onVisualizerSettingsChange,
  VISUALIZER_PRESETS = null,
  socketRef = null,
  user,
  voiceChatVolume = 0.8,
  onVoiceChatVolumeChange,
  musicVolume = 0.7,
  onMusicVolumeChange,
  onUsernameChange,
  isStreaming = false,
  onVideoPlay,
  onVideoPause,
  onVideoSkip
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLightingControls, setShowLightingControls] = useState(false);
  const [showVisualizerControls, setShowVisualizerControls] = useState(false);
  const [showUsernameChange, setShowUsernameChange] = useState(false);
  const [showVolumeControls, setShowVolumeControls] = useState(false);
  const [showVideoControls, setShowVideoControls] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  
  // Check if username change is allowed (30 day cooldown)
  const canChangeUsername = () => {
    if (!user?.username_changed_at) return true;
    const lastChange = new Date(user.username_changed_at);
    const now = new Date();
    const daysSinceChange = (now - lastChange) / (1000 * 60 * 60 * 24);
    return daysSinceChange >= 30;
  };
  
  const getDaysUntilChange = () => {
    if (!user?.username_changed_at) return 0;
    const lastChange = new Date(user.username_changed_at);
    const now = new Date();
    const daysSinceChange = (now - lastChange) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(30 - daysSinceChange));
  };
  
  const handleUsernameChangeSubmit = async () => {
    if (!newUsername.trim()) {
      setUsernameError('Username cannot be empty');
      return;
    }
    if (newUsername.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return;
    }
    if (newUsername.length > 20) {
      setUsernameError('Username must be 20 characters or less');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(newUsername)) {
      setUsernameError('Only letters, numbers, and underscores allowed');
      return;
    }
    
    setUsernameError('');
    const success = await onUsernameChange?.(newUsername.trim());
    if (success) {
      setShowUsernameChange(false);
      setNewUsername('');
    }
  };
  
  // Drag state
  const [pos, setPos] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const handleMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    hasMoved.current = false;
    setDragging(true);
    
    const onMouseMove = (moveEvent) => {
      hasMoved.current = true;
      const newX = moveEvent.clientX - dragOffset.current.x;
      const newY = moveEvent.clientY - dragOffset.current.y;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 60, newX)),
        y: Math.max(0, Math.min(window.innerHeight - 60, newY))
      });
    };
    
    const onMouseUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
    hasMoved.current = false;
    setDragging(true);
    
    const onTouchMove = (moveEvent) => {
      hasMoved.current = true;
      const t = moveEvent.touches[0];
      const newX = t.clientX - dragOffset.current.x;
      const newY = t.clientY - dragOffset.current.y;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 60, newX)),
        y: Math.max(0, Math.min(window.innerHeight - 60, newY))
      });
    };
    
    const onTouchEnd = () => {
      setDragging(false);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
    
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onTouchEnd);
  };

  const handleClick = () => {
    // Only open menu if we didn't drag
    if (!hasMoved.current) {
      soundFX?.click?.();
      setIsOpen(!isOpen);
    }
    hasMoved.current = false;
  };

  const handleLightingPreset = (preset) => {
    soundFX?.click?.();
    onLightingPresetChange?.(preset.id);
    onLightingFxChange?.({
      ...lightingFx,
      fx_mode: preset.id,
      intensity: preset.intensity,
      speed: preset.speed
    });
  };

  const handleIntensityChange = (value) => {
    onLightingFxChange?.({ ...lightingFx, intensity: value[0] });
  };

  const handleSpeedChange = (value) => {
    onLightingFxChange?.({ ...lightingFx, speed: value[0] });
  };

  // Video control handlers (admin only)
  const handleVideoPlay = async () => {
    soundFX?.click?.();
    setIsVideoPlaying(true);
    await onVideoPlay?.();
    toast.success('Video playing - synced to all');
  };
  
  const handleVideoPause = async () => {
    soundFX?.click?.();
    setIsVideoPlaying(false);
    await onVideoPause?.();
    toast.success('Video paused - synced to all');
  };
  
  const handleVideoSkip = async (seconds) => {
    soundFX?.click?.();
    await onVideoSkip?.(seconds);
    toast.success(`Skipped ${seconds > 0 ? 'forward' : 'back'} ${Math.abs(seconds)}s`);
  };

  const menuItems = [
    { 
      id: 'emotes', 
      label: 'Emotes', 
      icon: Sparkles, 
      color: 'from-yellow-500 to-orange-500',
      borderColor: 'border-yellow-500/50',
      onClick: () => { soundFX?.click?.(); setShowEmoteMenu(true); setIsOpen(false); }
    },
    ...(isAdmin ? [{
      id: 'lighting',
      label: 'Lighting',
      icon: Sun,
      color: 'from-purple-500 to-pink-500',
      borderColor: 'border-purple-500/50',
      onClick: () => { soundFX?.click?.(); setShowLightingControls(!showLightingControls); setShowVisualizerControls(false); setShowVolumeControls(false); }
    },
    {
      id: 'visualizer',
      label: 'Visualizer',
      icon: Palette,
      color: 'from-violet-500 to-purple-500',
      borderColor: 'border-violet-500/50',
      onClick: () => { soundFX?.click?.(); setShowVisualizerControls(!showVisualizerControls); setShowLightingControls(false); setShowVolumeControls(false); },
      badge: currentRoom === 'visualizer' ? 'ACTIVE' : null
    }] : []),
    { 
      id: 'voice', 
      label: voiceChatEnabled ? (isMicMuted ? 'Unmute Mic' : 'Mute Mic') : 'Voice Chat', 
      icon: voiceChatEnabled ? (isMicMuted ? MicOff : Mic) : Mic, 
      color: voiceChatEnabled ? (isMicMuted ? 'from-red-500 to-red-600' : 'from-green-500 to-emerald-500') : 'from-gray-500 to-gray-600',
      borderColor: voiceChatEnabled ? (isMicMuted ? 'border-red-500/50' : 'border-green-500/50') : 'border-gray-500/50',
      onClick: () => { soundFX?.click?.(); toggleMic?.(); }
    },
    { 
      id: 'quests', 
      label: 'Quests', 
      icon: Award, 
      color: 'from-amber-500 to-yellow-500',
      borderColor: 'border-amber-500/50',
      onClick: () => { soundFX?.click?.(); setShowQuestPanel(true); setIsOpen(false); }
    },
    { 
      id: 'leaderboard', 
      label: 'Leaderboard', 
      icon: Trophy, 
      color: 'from-yellow-400 to-amber-500',
      borderColor: 'border-yellow-400/50',
      onClick: () => { soundFX?.click?.(); setShowLeaderboard(true); setIsOpen(false); }
    },
    { 
      id: 'rooms', 
      label: 'Change Room', 
      icon: DoorOpen, 
      color: 'from-blue-500 to-cyan-500',
      borderColor: 'border-cyan-500/50',
      onClick: () => { soundFX?.click?.(); setShowRoomMenu(true); setIsOpen(false); },
      badge: currentRoom === 'rooftop' ? 'VIP' : null
    },
    { 
      id: 'avatar', 
      label: 'Avatar', 
      icon: User, 
      color: 'from-purple-500 to-violet-500',
      borderColor: 'border-purple-500/50',
      onClick: () => { soundFX?.click?.(); setShowAvatarEditor(true); setIsOpen(false); }
    },
    { 
      id: 'bar', 
      label: 'Virtual Bar', 
      icon: Wine, 
      color: 'from-rose-500 to-pink-500',
      borderColor: 'border-rose-500/50',
      onClick: () => { soundFX?.click?.(); setShowVirtualBar?.(true); setIsOpen(false); }
    },
    { 
      id: 'photo-mode', 
      label: 'Photo Mode', 
      icon: Camera, 
      color: 'from-sky-500 to-blue-500',
      borderColor: 'border-sky-500/50',
      onClick: () => { soundFX?.click?.(); onOpenPhotoMode?.(); setIsOpen(false); }
    },
    { 
      id: 'dj-queue', 
      label: 'DJ Queue', 
      icon: ListMusic, 
      color: 'from-fuchsia-500 to-pink-500',
      borderColor: 'border-fuchsia-500/50',
      onClick: () => { soundFX?.click?.(); onOpenDJQueue?.(); setIsOpen(false); }
    },
    { 
      id: 'dance-battle', 
      label: 'Dance Battle', 
      icon: Swords, 
      color: 'from-red-500 to-orange-500',
      borderColor: 'border-red-500/50',
      onClick: () => { soundFX?.click?.(); onOpenDanceBattle?.(); setIsOpen(false); }
    },
    { 
      id: 'friends', 
      label: 'Friends', 
      icon: Users, 
      color: 'from-cyan-500 to-teal-500',
      borderColor: 'border-cyan-500/50',
      onClick: () => { soundFX?.click?.(); onOpenFriendsPanel?.(); setIsOpen(false); }
    },
    { 
      id: 'daily', 
      label: 'Daily Rewards', 
      icon: Gift, 
      color: 'from-yellow-500 to-orange-500',
      borderColor: 'border-yellow-500/50',
      onClick: () => { soundFX?.click?.(); setShowDailyRewards?.(true); setIsOpen(false); },
      badge: !dailyBonusClaimed ? 'NEW' : null
    },
    { 
      id: 'private', 
      label: 'Private Rooms', 
      icon: Lock, 
      color: 'from-indigo-500 to-purple-500',
      borderColor: 'border-indigo-500/50',
      onClick: () => { soundFX?.click?.(); setShowPrivateRooms?.(true); setIsOpen(false); }
    },
    { 
      id: 'volume', 
      label: 'Volume Controls', 
      icon: Volume2, 
      color: 'from-cyan-500 to-blue-500',
      borderColor: 'border-cyan-500/50',
      onClick: () => { soundFX?.click?.(); setShowVolumeControls(!showVolumeControls); setShowLightingControls(false); setShowVisualizerControls(false); setShowVideoControls(false); }
    },
    { 
      id: 'username', 
      label: 'Change Username', 
      icon: Edit3, 
      color: canChangeUsername() ? 'from-teal-500 to-emerald-500' : 'from-gray-500 to-gray-600',
      borderColor: canChangeUsername() ? 'border-teal-500/50' : 'border-gray-500/50',
      onClick: () => { 
        soundFX?.click?.(); 
        if (canChangeUsername()) {
          setShowUsernameChange(true);
          setNewUsername(user?.username || '');
        }
      },
      badge: canChangeUsername() ? null : `${getDaysUntilChange()}d`,
      disabled: !canChangeUsername()
    },
  ];

  // Video controls for admin/owner/overseer only when streaming
  if (isAdmin && isStreaming) {
    menuItems.splice(4, 0, { // Insert after DJ Booth
      id: 'videoControl',
      label: 'Video Controls',
      icon: Play,
      color: 'from-red-500 to-rose-500',
      borderColor: 'border-red-500/50',
      onClick: () => { soundFX?.click?.(); setShowVideoControls(!showVideoControls); setShowLightingControls(false); setShowVisualizerControls(false); setShowVolumeControls(false); },
      badge: 'DJ'
    });
  }

  if (currentRoom === 'rooftop') {
    menuItems.push({
      id: 'weather',
      label: weatherEffect === 'rain' ? 'Stop Rain' : 'Make it Rain',
      icon: Cloud,
      color: weatherEffect === 'rain' ? 'from-blue-400 to-cyan-400' : 'from-gray-500 to-blue-500',
      borderColor: 'border-blue-500/50',
      onClick: () => { 
        soundFX?.click?.(); 
        setWeatherEffect?.(weatherEffect === 'rain' ? 'clear' : 'rain');
      }
    });
  }

  if (isAdmin || isDJ) {
    menuItems.push({ 
      id: 'announce', 
      label: 'Announce', 
      icon: Megaphone, 
      color: 'from-red-500 to-orange-500',
      borderColor: 'border-red-500/50',
      onClick: () => { soundFX?.click?.(); setShowAnnouncement?.(true); setIsOpen(false); }
    });
  }

  if (isAdmin) {
    menuItems.push({
      id: 'room-creator',
      label: 'Create Room',
      icon: Palette,
      color: 'from-cyan-500 to-purple-500',
      borderColor: 'border-cyan-500/50',
      onClick: () => { soundFX?.click?.(); onOpenRoomCreator?.(); setIsOpen(false); }
    });
    menuItems.push({ 
      id: 'admin', 
      label: 'Admin Panel', 
      icon: Shield, 
      color: 'from-red-500 to-red-600',
      borderColor: 'border-red-500/50',
      onClick: () => { soundFX?.click?.(); setShowAdminPanel(true); setIsOpen(false); }
    });
    menuItems.push({
      id: 'clear-chat',
      label: 'Clear Chat',
      icon: X,
      color: 'from-gray-500 to-gray-600',
      borderColor: 'border-gray-500/50',
      onClick: () => { 
        soundFX?.click?.(); 
        socketRef?.current?.emit('admin_clear_chat');
        toast.success('Chat cleared');
      }
    });
    menuItems.push({
      id: 'slow-mode',
      label: slowMode ? 'Disable Slow Mode' : 'Enable Slow Mode',
      icon: Clock,
      color: slowMode ? 'from-yellow-500 to-amber-500' : 'from-gray-500 to-gray-600',
      borderColor: slowMode ? 'border-yellow-500/50' : 'border-gray-500/50',
      onClick: () => { 
        soundFX?.click?.();
        const newVal = !slowMode;
        onSlowModeToggle?.(newVal);
        socketRef?.current?.emit('admin_slow_mode', { enabled: newVal });
        toast.success(newVal ? 'Slow mode ON (5s between messages)' : 'Slow mode OFF');
      },
      badge: slowMode ? 'ON' : null
    });
    menuItems.push({
      id: 'lock-room',
      label: roomLocked ? 'Unlock Club' : 'Lock Club',
      icon: roomLocked ? Unlock : Lock,
      color: roomLocked ? 'from-green-500 to-emerald-500' : 'from-red-500 to-rose-500',
      borderColor: roomLocked ? 'border-green-500/50' : 'border-red-500/50',
      onClick: () => {
        soundFX?.click?.();
        const newVal = !roomLocked;
        onRoomLockToggle?.(newVal);
        socketRef?.current?.emit('admin_lock_room', { locked: newVal });
        toast.success(newVal ? 'Club locked - no new entries' : 'Club unlocked');
      },
      badge: roomLocked ? 'LOCKED' : null
    });
  }

  // Determine button position
  const buttonStyle = pos.x !== null ? {
    position: 'fixed',
    left: pos.x,
    top: pos.y,
    right: 'auto',
    zIndex: 9999,
    cursor: dragging ? 'grabbing' : 'grab'
  } : {
    position: 'fixed',
    top: 190,
    right: 8,
    zIndex: 9999,
    cursor: 'grab'
  };

  return (
    <>
      {/* Hamburger Button - ALWAYS VISIBLE, Draggable in Fullscreen */}
      <div
        style={buttonStyle}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleClick}
        className={`w-14 h-14 rounded-full bg-gradient-to-br from-purple-900 to-black border-2 border-purple-400 hover:border-purple-300 shadow-xl shadow-purple-500/40 flex items-center justify-center transition-colors select-none touch-none ${dragging ? 'scale-110' : 'hover:scale-105'}`}
        data-testid="hamburger-menu-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-purple-300 pointer-events-none" />
        ) : (
          <Menu className="w-6 h-6 text-purple-300 pointer-events-none" />
        )}
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9997]"
        />
      )}

      {/* Slide-out Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-gradient-to-b from-gray-900/98 via-purple-900/30 to-gray-900/98 backdrop-blur-xl border-l border-purple-500/30 z-[9998] shadow-2xl"
          >
            {/* Header */}
            <div className="p-4 border-b border-purple-500/20">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Menu
                </h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-yellow-500/20 px-3 py-1 rounded-full">
                    <span className="text-yellow-400 text-sm">💰</span>
                    <span className="text-yellow-300 font-bold">{vibecoins}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-4 pb-40 space-y-2 overflow-y-auto max-h-[calc(100vh-100px)]">
              {menuItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <motion.button
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={item.onClick}
                    className={`w-full flex items-center gap-4 p-3 rounded-xl border ${item.borderColor} bg-black/30 hover:bg-white/5 transition-all group relative overflow-hidden`}
                    data-testid={`menu-item-${item.id}`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                    
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    
                    <span className="text-white font-medium flex-1 text-left">{item.label}</span>
                    
                    {item.badge && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        item.badge === 'LIVE' 
                          ? 'bg-red-500 text-white animate-pulse' 
                          : item.badge === 'VIP'
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                          : 'bg-green-500 text-white'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </motion.button>
                  
                  {/* Lighting Controls Panel */}
                  {item.id === 'lighting' && showLightingControls && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-gradient-to-br from-purple-900/40 to-pink-900/30 rounded-xl p-4 ml-2 mr-2 border border-purple-500/20 space-y-4"
                    >
                      <div className="space-y-2">
                        <label className="text-xs text-white/60 uppercase tracking-wider">Lighting Presets</label>
                        <div className="grid grid-cols-3 gap-2">
                          {LIGHTING_PRESETS.map(preset => (
                            <button
                              key={preset.id}
                              onClick={() => handleLightingPreset(preset)}
                              className={`p-2 rounded-lg border text-center transition-all ${
                                lightingPreset === preset.id
                                  ? 'border-pink-500 bg-pink-500/30 ring-1 ring-pink-400'
                                  : 'border-white/10 bg-black/20 hover:border-pink-500/50'
                              }`}
                              data-testid={`lighting-preset-${preset.id}`}
                            >
                              <div className="flex gap-0.5 justify-center mb-1">
                                {preset.colors.slice(0, 3).map((c, i) => (
                                  <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c }} />
                                ))}
                              </div>
                              <div className="text-[10px] text-white font-medium">{preset.name}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <label className="text-xs text-white/60">Brightness</label>
                          <span className="text-xs text-pink-400">{Math.round((lightingFx.intensity || 0.4) * 100)}%</span>
                        </div>
                        <Slider
                          value={[lightingFx.intensity || 0.4]}
                          onValueChange={handleIntensityChange}
                          min={0}
                          max={1}
                          step={0.05}
                          className="[&>span]:bg-pink-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <label className="text-xs text-white/60">Speed</label>
                          <span className="text-xs text-purple-400">{(lightingFx.speed || 0.5).toFixed(1)}x</span>
                        </div>
                        <Slider
                          value={[lightingFx.speed || 0.5]}
                          onValueChange={handleSpeedChange}
                          min={0.1}
                          max={2}
                          step={0.1}
                          className="[&>span]:bg-purple-500"
                        />
                      </div>
                      
                      {/* Auto-Cycle (DJ Light Show) */}
                      <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                        <div>
                          <span className="text-sm text-white/80">Auto-Cycle</span>
                          <p className="text-[10px] text-white/40">Rotate presets every 30s</p>
                        </div>
                        <button
                          onClick={() => { soundFX?.click?.(); onAutoCycleChange?.(!autoCycleLights); }}
                          className={`w-12 h-6 rounded-full transition-all ${
                            autoCycleLights ? 'bg-pink-500' : 'bg-gray-600'
                          }`}
                          data-testid="auto-cycle-toggle"
                        >
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                            autoCycleLights ? 'translate-x-6' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      
                      {/* DROP Button (admin/DJ dramatic transition) */}
                      {(isAdmin || isDJ) && (
                        <button
                          onClick={() => { soundFX?.click?.(); onDropTrigger?.(); }}
                          className="w-full p-3 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 rounded-lg text-white font-bold text-sm uppercase tracking-wider hover:from-red-500 hover:via-orange-400 hover:to-yellow-400 transition-all shadow-lg shadow-red-500/30 active:scale-95"
                          data-testid="drop-effect-btn"
                        >
                          DROP THE LIGHTS
                        </button>
                      )}
                    </motion.div>
                  )}
                  
                  {/* Visualizer Controls Panel */}
                  {item.id === 'visualizer' && showVisualizerControls && VISUALIZER_PRESETS && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-gradient-to-br from-violet-900/40 to-purple-900/30 rounded-xl p-4 ml-2 mr-2 border border-violet-500/20 space-y-4"
                    >
                      {/* Wall Visual Selector */}
                      <div className="space-y-2">
                        <label className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-2">
                          <span>🖼️</span> Wall Visual
                        </label>
                        <select 
                          value={visualizerSettings.wallVisual || 'bars'}
                          onChange={(e) => onVisualizerSettingsChange?.({ ...visualizerSettings, wallVisual: e.target.value })}
                          className="w-full bg-black/40 border border-violet-500/30 rounded-lg p-2 text-white text-sm"
                        >
                          {VISUALIZER_PRESETS.walls?.map(preset => (
                            <option key={preset.id} value={preset.id}>{preset.icon} {preset.name}</option>
                          ))}
                          <option value="custom">🔗 Custom URL</option>
                        </select>
                        {visualizerSettings.wallVisual === 'custom' && (
                          <input 
                            type="text"
                            placeholder="Enter GIF/Video URL..."
                            value={visualizerSettings.wallCustomUrl || ''}
                            onChange={(e) => onVisualizerSettingsChange?.({ ...visualizerSettings, wallCustomUrl: e.target.value })}
                            className="w-full bg-black/40 border border-violet-500/30 rounded-lg p-2 text-white text-sm mt-2"
                          />
                        )}
                      </div>
                      
                      {/* Floor Visual Selector */}
                      <div className="space-y-2">
                        <label className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-2">
                          <span>🟪</span> Floor Visual
                        </label>
                        <select 
                          value={visualizerSettings.floorVisual || 'grid'}
                          onChange={(e) => onVisualizerSettingsChange?.({ ...visualizerSettings, floorVisual: e.target.value })}
                          className="w-full bg-black/40 border border-violet-500/30 rounded-lg p-2 text-white text-sm"
                        >
                          {VISUALIZER_PRESETS.floor?.map(preset => (
                            <option key={preset.id} value={preset.id}>{preset.icon} {preset.name}</option>
                          ))}
                          <option value="custom">🔗 Custom URL</option>
                        </select>
                        {visualizerSettings.floorVisual === 'custom' && (
                          <input 
                            type="text"
                            placeholder="Enter GIF/Video URL..."
                            value={visualizerSettings.floorCustomUrl || ''}
                            onChange={(e) => onVisualizerSettingsChange?.({ ...visualizerSettings, floorCustomUrl: e.target.value })}
                            className="w-full bg-black/40 border border-violet-500/30 rounded-lg p-2 text-white text-sm mt-2"
                          />
                        )}
                      </div>
                      
                      {/* Ceiling Visual Selector */}
                      <div className="space-y-2">
                        <label className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-2">
                          <span>⬆️</span> Ceiling Visual
                        </label>
                        <select 
                          value={visualizerSettings.ceilingVisual || 'stars'}
                          onChange={(e) => onVisualizerSettingsChange?.({ ...visualizerSettings, ceilingVisual: e.target.value })}
                          className="w-full bg-black/40 border border-violet-500/30 rounded-lg p-2 text-white text-sm"
                        >
                          {VISUALIZER_PRESETS.ceiling?.map(preset => (
                            <option key={preset.id} value={preset.id}>{preset.icon} {preset.name}</option>
                          ))}
                          <option value="custom">🔗 Custom URL</option>
                        </select>
                      </div>
                      
                      {/* Color Scheme */}
                      <div className="space-y-2">
                        <label className="text-xs text-white/60 uppercase tracking-wider">Color Scheme</label>
                        <div className="grid grid-cols-3 gap-2">
                          {VISUALIZER_PRESETS.colorSchemes?.map(scheme => (
                            <button
                              key={scheme.id}
                              onClick={() => onVisualizerSettingsChange?.({ ...visualizerSettings, colorScheme: scheme.id })}
                              className={`p-2 rounded-lg border text-center transition-all ${
                                visualizerSettings.colorScheme === scheme.id
                                  ? 'border-violet-500 bg-violet-500/30'
                                  : 'border-white/10 bg-black/20 hover:border-violet-500/50'
                              }`}
                            >
                              <div className="flex gap-0.5 justify-center mb-1">
                                {scheme.colors.slice(0, 3).map((c, i) => (
                                  <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                                ))}
                              </div>
                              <div className="text-xs text-white">{scheme.name}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Intensity Slider */}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <label className="text-xs text-white/60">Intensity</label>
                          <span className="text-xs text-violet-400">{Math.round((visualizerSettings.intensity || 0.7) * 100)}%</span>
                        </div>
                        <Slider
                          value={[visualizerSettings.intensity || 0.7]}
                          onValueChange={(value) => onVisualizerSettingsChange?.({ ...visualizerSettings, intensity: value[0] })}
                          min={0.1}
                          max={1}
                          step={0.05}
                          className="[&>span]:bg-violet-500"
                        />
                      </div>
                      
                      {/* Sync to Audio Toggle */}
                      <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                        <span className="text-sm text-white/80">🎵 Sync to Music</span>
                        <button
                          onClick={() => onVisualizerSettingsChange?.({ ...visualizerSettings, syncToAudio: !visualizerSettings.syncToAudio })}
                          className={`w-12 h-6 rounded-full transition-all ${
                            visualizerSettings.syncToAudio !== false ? 'bg-violet-500' : 'bg-gray-600'
                          }`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                            visualizerSettings.syncToAudio !== false ? 'translate-x-6' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      
                      {/* Go to Visualizer Room button */}
                      {currentRoom !== 'visualizer' && (
                        <button
                          onClick={() => { setShowRoomMenu?.(true); setIsOpen(false); }}
                          className="w-full p-3 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg text-white font-medium hover:from-violet-500 hover:to-purple-500 transition-all"
                        >
                          🎨 Enter Visualizer Room
                        </button>
                      )}
                    </motion.div>
                  )}
                  
                  {/* Volume Controls Panel */}
                  {item.id === 'volume' && showVolumeControls && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-gradient-to-br from-cyan-900/40 to-blue-900/30 rounded-xl p-4 ml-2 mr-2 border border-cyan-500/20 space-y-4"
                      data-testid="volume-controls-panel"
                    >
                      {/* Voice Chat Volume */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <label className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-1.5">
                            <Mic className="w-3 h-3" /> Voice Chat
                          </label>
                          <span className="text-xs text-cyan-400 font-medium">{Math.round(voiceChatVolume * 100)}%</span>
                        </div>
                        <Slider
                          value={[voiceChatVolume]}
                          onValueChange={(value) => onVoiceChatVolumeChange?.(value[0])}
                          min={0}
                          max={1}
                          step={0.05}
                          className="[&>span]:bg-cyan-500"
                          data-testid="voice-volume-slider"
                        />
                        <p className="text-[10px] text-white/40">Controls how loud nearby players sound</p>
                      </div>
                      
                      {/* Music / Video Volume */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <label className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-1.5">
                            <Music className="w-3 h-3" /> Music / Video
                          </label>
                          <span className="text-xs text-blue-400 font-medium">{Math.round(musicVolume * 100)}%</span>
                        </div>
                        <Slider
                          value={[musicVolume]}
                          onValueChange={(value) => onMusicVolumeChange?.(value[0])}
                          min={0}
                          max={1}
                          step={0.05}
                          className="[&>span]:bg-blue-500"
                          data-testid="music-volume-slider"
                        />
                        <p className="text-[10px] text-white/40">Controls YouTube player & DJ stream volume</p>
                      </div>
                      
                      {/* Quick presets */}
                      <div className="grid grid-cols-3 gap-2 pt-1">
                        <button
                          onClick={() => { onVoiceChatVolumeChange?.(0); onMusicVolumeChange?.(0); }}
                          className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-red-500/50 text-center transition-all"
                          data-testid="volume-mute-all"
                        >
                          <VolumeX className="w-4 h-4 mx-auto text-red-400" />
                          <div className="text-[10px] text-white/60 mt-1">Mute All</div>
                        </button>
                        <button
                          onClick={() => { onVoiceChatVolumeChange?.(0.5); onMusicVolumeChange?.(0.5); }}
                          className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-cyan-500/50 text-center transition-all"
                          data-testid="volume-balanced"
                        >
                          <Volume2 className="w-4 h-4 mx-auto text-cyan-400" />
                          <div className="text-[10px] text-white/60 mt-1">Balanced</div>
                        </button>
                        <button
                          onClick={() => { onVoiceChatVolumeChange?.(1); onMusicVolumeChange?.(1); }}
                          className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-green-500/50 text-center transition-all"
                          data-testid="volume-max-all"
                        >
                          <Volume2 className="w-4 h-4 mx-auto text-green-400" />
                          <div className="text-[10px] text-white/60 mt-1">Max</div>
                        </button>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Video Controls Panel (Admin Only) */}
                  {item.id === 'videoControl' && showVideoControls && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-gradient-to-br from-red-900/40 to-rose-900/30 rounded-xl p-4 ml-2 mr-2 border border-red-500/20 space-y-4"
                      data-testid="video-controls-panel"
                    >
                      <div className="text-center mb-2">
                        <h4 className="text-sm font-bold text-white flex items-center justify-center gap-2">
                          <Play className="w-4 h-4 text-red-400" />
                          DJ Video Controls
                        </h4>
                        <p className="text-[10px] text-white/50 mt-1">Controls sync to all players</p>
                      </div>
                      
                      {/* Play/Pause */}
                      <div className="flex justify-center gap-4">
                        <button
                          onClick={handleVideoPause}
                          className={`p-4 rounded-xl border transition-all ${
                            !isVideoPlaying 
                              ? 'border-red-500 bg-red-500/30' 
                              : 'border-white/10 bg-black/20 hover:border-red-500/50'
                          }`}
                          data-testid="video-pause-btn"
                        >
                          <Pause className="w-6 h-6 text-white" />
                          <div className="text-xs text-white/70 mt-1">Pause</div>
                        </button>
                        <button
                          onClick={handleVideoPlay}
                          className={`p-4 rounded-xl border transition-all ${
                            isVideoPlaying 
                              ? 'border-green-500 bg-green-500/30' 
                              : 'border-white/10 bg-black/20 hover:border-green-500/50'
                          }`}
                          data-testid="video-play-btn"
                        >
                          <Play className="w-6 h-6 text-white" />
                          <div className="text-xs text-white/70 mt-1">Play</div>
                        </button>
                      </div>
                      
                      {/* Skip Controls */}
                      <div className="space-y-2">
                        <label className="text-xs text-white/60 text-center block">Skip Video</label>
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleVideoSkip(-30)}
                            className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-rose-500/50 text-center transition-all"
                            data-testid="video-skip-back-30"
                          >
                            <SkipBack className="w-4 h-4 mx-auto text-rose-400" />
                            <div className="text-[10px] text-white/60 mt-1">-30s</div>
                          </button>
                          <button
                            onClick={() => handleVideoSkip(-10)}
                            className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-rose-500/50 text-center transition-all"
                            data-testid="video-skip-back-10"
                          >
                            <SkipBack className="w-4 h-4 mx-auto text-rose-400" />
                            <div className="text-[10px] text-white/60 mt-1">-10s</div>
                          </button>
                          <button
                            onClick={() => handleVideoSkip(10)}
                            className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-red-500/50 text-center transition-all"
                            data-testid="video-skip-forward-10"
                          >
                            <SkipForward className="w-4 h-4 mx-auto text-red-400" />
                            <div className="text-[10px] text-white/60 mt-1">+10s</div>
                          </button>
                          <button
                            onClick={() => handleVideoSkip(30)}
                            className="p-2 rounded-lg border border-white/10 bg-black/20 hover:border-red-500/50 text-center transition-all"
                            data-testid="video-skip-forward-30"
                          >
                            <SkipForward className="w-4 h-4 mx-auto text-red-400" />
                            <div className="text-[10px] text-white/60 mt-1">+30s</div>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Username Change Modal */}
      <AnimatePresence>
        {showUsernameChange && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setShowUsernameChange(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-gray-900 to-gray-800 border border-purple-500/30 rounded-2xl p-6 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-purple-400" />
                Change Username
              </h3>
              <p className="text-sm text-white/60 mb-4">
                You can change your username once every 30 days.
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Current Username</label>
                  <div className="p-2 bg-black/30 rounded-lg text-white/50 text-sm">
                    {user?.username || 'Not set'}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-white/70 mb-1 block">New Username</label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => {
                      setNewUsername(e.target.value);
                      setUsernameError('');
                    }}
                    placeholder="Enter new username"
                    className="w-full p-3 bg-black/50 border border-purple-500/30 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
                    maxLength={20}
                  />
                  {usernameError && (
                    <p className="text-red-400 text-xs mt-1">{usernameError}</p>
                  )}
                  <p className="text-xs text-white/40 mt-1">
                    3-20 characters, letters, numbers, and underscores only
                  </p>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowUsernameChange(false)}
                    className="flex-1 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUsernameChangeSubmit}
                    disabled={!newUsername.trim() || newUsername === user?.username}
                    className="flex-1 p-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Change Username
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HamburgerMenu;
